<template>
    <DefaultHeader />
    <div class="container-fluid">
        <div class="row d-flex justify-content-center">
            <div class="col-12 col-xxl-10 mb-3 mb-lg-5">
                <div class="card table-card shadow-sm">
                    <div class="card-header bg-white">
                        <div class="row g-2 align-items-end">
                            <div class="col-auto">
                                <h5 class="mb-0">Logs — Missions</h5>
                            </div>
                            <div class="col-auto ms-auto">
                                <label class="form-label mb-1 small">Mission ID</label>
                                <input type="number" class="form-control form-control-sm" v-model.number="qMissionId"
                                    placeholder="ex: 42" />
                            </div>
                            <div class="col-auto">
                                <label class="form-label mb-1 small">Action</label>
                                <input class="form-control form-control-sm" v-model.trim="qAction"
                                    placeholder="ex: start, add_member…" />
                            </div>
                            <div class="col-auto">
                                <label class="form-label mb-1 small">Méthode</label>
                                <select class="form-select form-select-sm" v-model="qMethod">
                                    <option value="">— Toutes —</option>
                                    <option>POST</option>
                                    <option>PUT</option>
                                    <option>DELETE</option>
                                </select>
                            </div>
                            <div class="col-auto">
                                <button class="btn btn-sm btn-primary" @click="reload">Rechercher</button>
                            </div>
                            <div class="col-auto">
                                <button class="btn btn-sm btn-outline-secondary"
                                    @click="resetFilters">Réinitialiser</button>
                            </div>
                        </div>
                    </div>

                    <div class="table-responsive">
                        <table class="table table-striped align-middle mb-0">
                            <thead class="small text-uppercase bg-body text-muted">
                                <tr>
                                    <th>Date</th>
                                    <th>Utilisateur</th>
                                    <th>Action</th>
                                    <th>Méthode</th>
                                    <th>Statut</th>
                                    <th>Mission</th>
                                    <th>Route</th>
                                    <th>IP</th>
                                    <th class="text-end">Payload</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-if="logs.length">
                                    <template v-for="log in filteredLogs" :key="log.id">
                                        <tr>
                                            <td class="small">{{ $filters.secondDateFormat(log.createdAt) }}</td>
                                            <td class="small">#{{ log.userId ?? '—' }}</td>
                                            <td class="small">{{ log.action || '—' }}</td>
                                            <td class="small">{{ log.method }}</td>
                                            <td class="small">
                                                <span :class="badgeClass(log.statusCode)" class="badge rounded-pill">
                                                    {{ log.statusCode }}
                                                </span>
                                            </td>
                                            <td class="small">#{{ log.entityId || log.missionId || '—' }}</td>
                                            <td class="small text-truncate" style="max-width: 360px" :title="log.route">
                                                {{
                                                    log.route }}</td>
                                            <td class="small">{{ log.ip || '—' }}</td>
                                            <td class="text-end">
                                                <button class="btn btn-sm btn-outline-secondary"
                                                    @click="togglePayload(log.id)">
                                                    {{ opened.has(log.id) ? 'Masquer' : 'Voir' }}
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-if="opened.has(log.id)" :key="`payload-${log.id}`">
                                            <td colspan="9">
                                                <pre class="mb-0 small"
                                                    style="white-space: pre-wrap">{{ formatPayload(log.payload) }}</pre>
                                            </td>
                                        </tr>
                                    </template>
                                </template>
                                <tr v-else>
                                    <td colspan="9" class="text-center text-muted py-4">Aucun log.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="card-footer bg-white d-flex justify-content-between align-items-center">
                        <div class="small text-muted">
                            {{ logs.length }} ligne(s) affichée(s)
                        </div>
                        <div class="d-flex gap-2">
                            <button class="btn btn-sm btn-outline-primary" :disabled="loading" @click="loadMore">
                                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                                Charger plus
                            </button>
                            <button class="btn btn-sm btn-outline-secondary" :disabled="!canReset" @click="softReset">
                                Rafraîchir
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>
            </div>
        </div>
    </div>
    <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue';
import DefaultFooter from '@/components/DefaultFooter.vue';
import AuditService from '@/services/auditLog.service';

export default {
    components: { DefaultHeader, DefaultFooter },
    data() {
        return {
            logs: [],
            loading: false,
            error: '',
            // pagination
            limit: 50,
            offset: 0,
            // filtres front additionnels
            qMissionId: '',
            qAction: '',
            qMethod: '',
            opened: new Set(),
        };
    },
    computed: {
        canReset() {
            return this.logs.length > 0;
        },
        filteredLogs() {
            return this.logs.filter(l => {
                if (this.qAction && !String(l.action || '').toLowerCase().includes(this.qAction.toLowerCase())) return false;
                if (this.qMethod && l.method !== this.qMethod) return false;
                if (this.qMissionId) {
                    const mid = Number(this.qMissionId);
                    const eid = Number(l.entityId || l.missionId);
                    if (!eid || eid !== mid) return false;
                }
                return true;
            });
        }
    },
    mounted() {
        this.fetch();
    },
    methods: {
        async fetch(reset = false) {
            try {
                this.loading = true;
                if (reset) { this.offset = 0; this.logs = []; }
                const params = { limit: this.limit, offset: this.offset };
                // côté API, /audit/missionlogs renvoie déjà uniquement les logs 'mission'
                const { data } = await AuditService.getMissionLogs(params);
                const rows = Array.isArray(data) ? data : [];
                if (reset) this.logs = rows;
                else this.logs = this.logs.concat(rows);
                this.offset += rows.length;
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erreur de chargement des logs';
            } finally {
                this.loading = false;
            }
        },
        reload() { this.fetch(true); },
        loadMore() { this.fetch(false); },
        softReset() { this.reload(); },
        resetFilters() {
            this.qMissionId = ''; this.qAction = ''; this.qMethod = '';
            this.reload();
        },
        togglePayload(id) {
            if (this.opened.has(id)) this.opened.delete(id);
            else this.opened.add(id);
        },
        formatPayload(p) {
            if (!p) return '—';
            try { return JSON.stringify(p, null, 2); } catch { return String(p); }
        },
        badgeClass(code) {
            if (code >= 500) return 'bg-danger';
            if (code >= 400) return 'bg-warning text-dark';
            if (code >= 200) return 'bg-success';
            return 'bg-secondary';
        }
    }
};
</script>
